import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { heroiconsWarning } from '@ld.consumer-portal/data-uri';
import { PlatformService } from '@ld.consumer-portal/utils';

export interface UnsavedChangesDialogData {
  type?: 'leave-page' | 'confirm-not-saved';
  title?: string;
  message?: string;
  stayButtonLabel?: string;
  leaveButtonLabel?: string;
}

export type UnsavedChangesDialogResult = 'stay' | 'leave';

@Component({
  selector: 'ld-unsaved-changes-dialog',
  template: `
    <div
      class="bg-white w-full rounded-t-2xl shadow-xl flex flex-col overflow-hidden bottom-sheet-slide-up"
      style="padding-bottom: env(safe-area-inset-bottom, 0px)"
      *transloco="let t"
    >
      <div class="flex items-center justify-between px-4 py-3 bg-black rounded-t-2xl shrink-0">
        <div class="flex items-center gap-2">
          <img [src]="warningIcon" alt="" class="w-5 h-5" aria-hidden="true" />
          <span class="font-dm text-base font-semibold text-white">
            {{ data.title ?? t(titleKey) }}
          </span>
        </div>
        <button
          type="button"
          class="flex items-center justify-center size-8 bg-transparent border-none cursor-pointer text-white"
          aria-label="Close"
          (click)="onStay()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="px-6 py-6">
        <p class="font-dm text-sm text-ld-neutral-500 m-0">
          {{ data.message ?? t(messageKey) }}
        </p>
      </div>

      <div class="px-6 pb-6 flex flex-col gap-3">
        <button
          id="unsaved-changes-dialog-stay-btn"
          type="button"
          class="btn-primary-ld-purple w-full"
          (click)="onStay()"
        >
          {{ data.stayButtonLabel ?? t(stayButtonKey) }}
        </button>

        <button
          id="unsaved-changes-dialog-leave-btn"
          type="button"
          class="btn-outline-ld-purple w-full"
          (click)="onLeave()"
        >
          {{ data.leaveButtonLabel ?? t(leaveButtonKey) }}
        </button>
      </div>
    </div>
  `,
  styles: `
    .bottom-sheet-slide-up {
      animation: bottomSheetSlideUp 0.3s ease-out;
    }
    @keyframes bottomSheetSlideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
  `,
  imports: [MatIconModule, TranslocoDirective],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnsavedChangesDialogComponent {
  private readonly dialogRef = inject<DialogRef<UnsavedChangesDialogResult>>(DialogRef);
  private readonly platformService = inject(PlatformService);
  protected readonly data = inject<UnsavedChangesDialogData>(DIALOG_DATA);

  protected readonly warningIcon = heroiconsWarning;

  constructor() {
    if (this.platformService.isNativeMobile()) {
      this.dialogRef.overlayRef.updateSize({ width: '100vw', maxWidth: '100vw' });
    }
  }

  protected get titleKey(): string {
    return this.data.type === 'confirm-not-saved'
      ? 'modalsUnsavedChangesConfirmNotSavedTitle'
      : 'modalsUnsavedChangesLeavePageTitle';
  }

  protected get messageKey(): string {
    return this.data.type === 'confirm-not-saved'
      ? 'modalsUnsavedChangesConfirmNotSavedMessage'
      : 'modalsUnsavedChangesLeavePageMessage';
  }

  protected get stayButtonKey(): string {
    return this.data.type === 'confirm-not-saved'
      ? 'modalsUnsavedChangesConfirmNotSavedStayButton'
      : 'modalsUnsavedChangesLeavePageStayButton';
  }

  protected get leaveButtonKey(): string {
    return this.data.type === 'confirm-not-saved'
      ? 'modalsUnsavedChangesConfirmNotSavedLeaveButton'
      : 'modalsUnsavedChangesLeavePageLeaveButton';
  }

  protected onStay(): void {
    this.dialogRef.close('stay');
  }

  protected onLeave(): void {
    this.dialogRef.close('leave');
  }
}
